import { useState, useMemo, useCallback, useRef } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { AtlasMap } from "@/components/atlas/AtlasMap";
import { LiveFleetSidebar } from "@/components/atlas/LiveFleetSidebar";
import { AtlasFilterBar, AtlasFilters } from "@/components/atlas/AtlasFilterBar";
import { useAtlasData } from "@/hooks/useAtlasData";
import { useSamsaraVehicles } from "@/hooks/useSamsaraVehicles";
import { SamsaraVehicle } from "@/hooks/useSamsaraVehicles";

import { Skeleton } from "@/components/ui/skeleton";
import { Maximize } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ExpandingSearch } from "@/components/ui/expanding-search";
import { SearchChip } from "@/components/ui/search-chip";

const DEFAULT_FILTERS: AtlasFilters = {
  locationIds: [],
  availability: "all",
  shift: "all",
  certification: "all",
  baseTrucks: [],
};

/** Approx distance in meters between two lat/lng points (Haversine). */
function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Radius in meters for "near a location" — 50 km covers a metro area. */
const LOCATION_FILTER_RADIUS = 50_000;

export default function Atlas() {
  const { data: liveVehicles = [], isLoading: vehiclesLoading, dataUpdatedAt } = useSamsaraVehicles();

  // Extract in-transit tractor names for deduplication
  const inTransitTractorNames = useMemo(
    () => liveVehicles.map((v) => v.name),
    [liveVehicles]
  );

  const { atlasLocations, isLoading } = useAtlasData(inTransitTractorNames);
  const [filters, setFilters] = useState<AtlasFilters>(DEFAULT_FILTERS);
  const [selectedVehicle, setSelectedVehicle] = useState<SamsaraVehicle | null>(null);
  const [fleetSearchQuery, setFleetSearchQuery] = useState("");
  const resetViewRef = useRef<(() => void) | null>(null);
  const handleResetViewReady = useCallback((fn: () => void) => { resetViewRef.current = fn; }, []);

  const ymsLocations = useMemo(
    () => atlasLocations.filter((l) => l.track_in_yms),
    [atlasLocations]
  );


  // Apply location and platform filters to vehicles
  const filteredVehicles = useMemo(() => {
    let result = liveVehicles;

    // Filter by proximity to selected locations
    if (filters.locationIds.length > 0) {
      const selectedLocs = atlasLocations.filter((l) => filters.locationIds.includes(l.id));
      if (selectedLocs.length > 0) {
        result = result.filter((v) =>
          v.lat != null &&
          v.lng != null &&
          selectedLocs.some(
            (loc) => haversineMeters(loc.lat, loc.lng, v.lat, v.lng) <= LOCATION_FILTER_RADIUS
          )
        );
      }
    }

    // Filter by base truck makes
    if (filters.baseTrucks.length > 0) {
      result = result.filter((v) => v.baseTruck && filters.baseTrucks.includes(v.baseTruck));
    }

    return result;
  }, [liveVehicles, filters.locationIds, filters.baseTrucks, atlasLocations]);

  // Map certification filter to capability filter for live vehicles
  const vehicleCapabilityFilter = filters.certification as "all" | "VO" | "NVO" | "Manual";

  return (
    <AppLayout title="In Transit">
      <div className="flex flex-col -m-6 h-[calc(100vh-4rem)]">
        {/* Filter row */}
        <div className="flex items-center gap-4 px-4 py-2 border-b">
          <AtlasFilterBar
            filters={filters}
            onFiltersChange={setFilters}
            locations={atlasLocations.map((l) => ({
              id: l.id,
              name: l.name,
              short_code: l.short_code,
              city: l.city,
            }))}
          />
          <SearchChip query={fleetSearchQuery} onClear={() => setFleetSearchQuery("")} />
          <div className="ml-auto flex items-center gap-2">
            <ExpandingSearch value={fleetSearchQuery} onChange={setFleetSearchQuery} placeholder="Search trucks..." />
          </div>
        </div>
        <div className="flex flex-1 min-h-0">
          <LiveFleetSidebar
            vehicles={filteredVehicles}
            onVehicleClick={setSelectedVehicle}
            dataUpdatedAt={dataUpdatedAt}
            capabilityFilter={vehicleCapabilityFilter}
            searchQuery={fleetSearchQuery}
            selectedVehicleId={selectedVehicle?.id ?? null}
          />
          <div className="flex-1 relative">
            {isLoading ? (
              <div className="h-full w-full flex items-center justify-center">
                <Skeleton className="h-full w-full rounded-lg" />
              </div>
            ) : (
              <AtlasMap
                locations={atlasLocations}
                selectedLocationId={filters.locationIds.length === 1 ? filters.locationIds[0] : null}
                liveVehicles={filteredVehicles}
                selectedVehicle={selectedVehicle}
                openPopupVehicleId={selectedVehicle?.id ?? null}
                vehicleCapabilityFilter={vehicleCapabilityFilter}
                ymsLocations={ymsLocations}
                onResetViewReady={handleResetViewReady}
              />
            )}

            {/* Reframe button — stacked vertically below Leaflet's default zoom in/out controls (top-left).
                Leaflet touch zoom buttons are 30x30 (two stacked + 1px border = ~61px) starting at top:10px,
                so this sits at top:83px with a 12px gap, matching their 30x30 footprint. */}
            <Button
              variant="outline"
              size="icon"
              className="absolute top-[83px] left-[10px] z-[1000] bg-card/90 backdrop-blur-sm shadow-sm h-[30px] w-[30px] flex items-center justify-center p-0"
              onClick={() => resetViewRef.current?.()}
              aria-label="Reset map zoom"
            >
              <Maximize className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
